import { FormattedMessage } from 'react-intl'

import {
  Case,
  CaseRelationshipType,
  ExternalIdNames,
  Maybe,
  MortgageReason,
  useGetCaseRelatedCasesQuery,
} from '@acre/graphql'
import { Button, BUTTON_TAG, BUTTON_TYPE_BUTTON } from '@acre/design-system'

export const useReferralSourceCase = (caseDetails?: Case) => {
  const referralSourceId = caseDetails?.related_cases?.find((relatedCase) => {
    return relatedCase.relationship_type === CaseRelationshipType.ReferralSource
  })?.case_id

  const { data, loading, error } = useGetCaseRelatedCasesQuery({
    variables: {
      id: referralSourceId!,
    },
    skip: !referralSourceId,
  })

  return { data, loading, error }
}

export const mortgageReasonMap = {
  [MortgageReason.ReasonFtb]: 'mortgageReason.reasonFtb',
  [MortgageReason.ReasonHouseMove]: 'mortgageReason.reasonHouseMove',
  [MortgageReason.ReasonRemortgage]: 'mortgageReason.reasonRemortgage',
  [MortgageReason.ReasonBtl]: 'mortgageReason.reasonBtl',
  [MortgageReason.ReasonBtlRemortgage]: 'mortgageReason.reasonBtlRemortgage',
  [MortgageReason.ReasonEquityRelease]: 'mortgageReason.reasonEquityRelease',
  [MortgageReason.ReasonFurtherAdvance]: 'mortgageReason.reasonFurtherAdvance',
  [MortgageReason.ReasonBusinessProtection]: 'mortgageReason.reasonBusinessProtection',
  [MortgageReason.ReasonProtection]: 'mortgageReason.reasonProtection',
  [MortgageReason.InvalidMortgageReason]: 'mortgageReason.invalidMortgageReason',
  [MortgageReason.ReasonCommercial]: 'mortgageReason.reasonCommercial',
  [MortgageReason.ReasonBridging]: 'mortgageReason.reasonBridging',
  [MortgageReason.ReasonGeneralInsurance]: 'mortgageReason.reasonGeneralInsurance',
}

type ExternalIdGroup = {
  name: Maybe<ExternalIdNames>
  ids: [Maybe<string>]
  id?: Maybe<String>
  source?: {
    id: Maybe<String>
    name: Maybe<ExternalIdNames>
  }
}[]

// Generic type for external objects based on shared structure across GraphQL types, e.g. ExternalCase, ExternalClient, etc.
export type GenericExternalType = {
  id: string
  source: {
    id: string
    name: Maybe<ExternalIdNames>
  }
}

const findExistingGroup = (acc: ExternalIdGroup, currentValue: Maybe<GenericExternalType>) => {
  return acc.find((group) => group.name === currentValue?.source?.name)
}

export const getGroupedList = (list: Maybe<Maybe<GenericExternalType>[]>): ExternalIdGroup => {
  if (!list) return []

  return list.filter(Boolean).reduce<ExternalIdGroup>((acc, currentValue) => {
    const existingGroup = findExistingGroup(acc, currentValue)
    if (existingGroup) {
      existingGroup.ids.push(currentValue?.source?.id)
    } else {
      acc.push({
        name: currentValue?.source?.name,
        ids: [currentValue?.source?.id],
      })
    }
    return acc
  }, [])
}

export const setListValueForExternal = (
  clearMandates: () => void,
  loading: boolean,
  source: Maybe<ExternalIdNames>,
  ids?: [Maybe<string>],
) => {
  if (source !== ExternalIdNames.GocardlessMandate) {
    return setListValue(ids)
  }
  return (
    <ul>
      {ids?.map((id) => (
        <li key={id}>
          {id}{' '}
          {id?.startsWith('BR') /* Only when it's a billing request not a full mandate */ ? (
            <Button
              id="ClearGCMandateButton"
              type={BUTTON_TYPE_BUTTON}
              buttonStyle={BUTTON_TAG}
              onClick={clearMandates}
              isLoading={loading}
            >
              <FormattedMessage id="caseOverview.flagsReview.button.clear" />
            </Button>
          ) : (
            ''
          )}
        </li>
      ))}
    </ul>
  )
}

export const setListValue = (ids?: [Maybe<string>]) => {
  return <ul>{ids?.map((id) => <li key={id}>{id}</li>)}</ul>
}

export const getExternalIdsConfigs = (
  clearMandates: () => void,
  updatingClients: boolean,
  lists: Record<string, ExternalIdGroup | undefined>,
) => [
  {
    list: lists.groupedExternalCaseList,
    idPrefix: 'OverviewExternalCaseIds',
    valueFn: setListValue,
    summaryKey: 'caseId',
  },
  {
    list: lists.groupedExternalClientList,
    idPrefix: 'OverviewExternalClientIds',
    valueFn: (ids: [Maybe<string>], name?: Maybe<ExternalIdNames>) =>
      setListValueForExternal(clearMandates, updatingClients, name, ids),
    summaryKey: 'clientIds',
  },
  {
    list: lists.groupedExternalPropertyList,
    idPrefix: 'OverviewExternalPropertyIds',
    valueFn: setListValue,
    summaryKey: 'propertyIds',
  },
  {
    list: lists.groupedExternalMortgageList,
    idPrefix: 'OverviewExternalMortgageIds',
    valueFn: setListValue,
    summaryKey: 'mortgageIds',
  },
  {
    list: lists.groupedExternalGiProductList,
    idPrefix: 'OverviewExternalGiProductIds',
    valueFn: setListValue,
    summaryKey: 'homeInsuranceIds',
  },
  {
    list: lists.groupedExternalProtectionProductList,
    idPrefix: 'OverviewExternalProtectionProductIds',
    valueFn: setListValue,
    summaryKey: 'protectionIds',
  },
]

// When TypeScript sees <T> in a .tsx file, it might confuse it for a JSX element instead of a type.
// Adding a comma after T (<T,>) clarifies that T is a generic type, not a JSX tag, to avoid confusion.
export const getExternalList = <T,>(
  list: T[] | null | undefined,
  getExternal: (item: T) => any[] | null | undefined,
): Maybe<Maybe<GenericExternalType>[]> => {
  return list?.flatMap((item) => getExternal(item) || []).filter(Boolean) ?? []
}
